import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useLocation, useParams } from 'react-router-dom'
import clsx from 'clsx'
import qs from 'query-string'
import { get, sortBy } from 'lodash'
import { DateTime } from 'luxon'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { ThemeProvider, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import BackButton from 'components/back-button'
import SchedulePageBackground from '../../components/page-background'
import VerifyAccessCode from 'components/verify-access-code'
import { useAuth } from 'lib/auth'
import { gtmEvent } from 'lib/gtm'
import themes, { extendTheme } from 'themes'
import { toThreeLetterTimezone } from 'utils/timezones'
import NoTimesAvailable from '../no-times-available'
import OverwriteAvailabilityDialog from '../overwrite-availability-dialog'
import { useStyles as useLegacyStyles } from '../styles'
import {
  aggregateByDate,
  buildDateStripDays,
  countByDate,
  dateRangeForMonth,
  groupSlotsByDayPart,
  initialAvailabilityDateRange,
  resolveSelectedDateFromQuery,
} from '../utils'
import BookingSummaryCard, {
  MobileBookingSummary,
  formatSelectedSlot,
} from './booking-summary-card'
import CalendarPopover from './calendar-popover'
import DateStrip from './date-strip'
import TimeSlotGroups from './time-slot-groups'
import useStyles, { PAGE_BACKGROUND } from './styles'

// Bay reservations carry their own list of times and use a bare `?` query;
// everything else reads the lesson type's available times.
const getSlotSource = (lessonTypes, nextExpirationDate) => {
  const spaceAvailableTimes = get(lessonTypes, [
    '0',
    'practiceSessionTypes',
    '0',
    'availableTimes',
  ])
  const isBayReservation = !!spaceAvailableTimes
  const timeSlots = isBayReservation
    ? sortBy(spaceAvailableTimes)
    : sortBy(
      (get(lessonTypes, ['0', 'availableTimes']) || []).map(
        t => t.startDateTime,
      ),
    )
  const filteredTimeSlots = nextExpirationDate
    ? timeSlots.filter(
      ts => DateTime.fromISO(ts) <= DateTime.fromISO(nextExpirationDate),
    )
    : timeSlots
  return { isBayReservation, timeSlots, filteredTimeSlots }
}

const SelectTimeContent = ({
  lessonTypes,
  currentUrl,
  previousUrl,
  refetch,
  setShowRequestTime,
  lessonCreditBalanceQuantity,
  nextExpirationDate,
}) => {
  const classes = useStyles()
  const legacyClasses = useLegacyStyles()
  const theme = useTheme()
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'))
  const { user } = useAuth()
  const { idOrSlug } = useParams()
  const location = useLocation()

  const lessonType = lessonTypes[0]
  const coach = lessonType.coach
  const timezone = get(lessonType, ['facilities', '0', 'timezone'])
  const durationInMinutes = lessonType.durationInMinutes
  const requiresAccessCode = lessonType.requiresAccessCode

  const { isBayReservation, timeSlots, filteredTimeSlots } = getSlotSource(
    lessonTypes,
    nextExpirationDate,
  )
  const searchQuery = isBayReservation ? '?startDateTime=' : '&startDateTime='

  const timeSlotsByDate = useMemo(
    () => aggregateByDate(filteredTimeSlots, timezone),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredTimeSlots.join(','), timezone],
  )
  const countsByDate = useMemo(() => countByDate(timeSlotsByDate), [
    timeSlotsByDate,
  ])

  const { selectedDay } = qs.parse(location.search)
  const [selectedDate, setSelectedDate] = useState(() => {
    const datesWithAvailability = Object.keys(
      aggregateByDate(
        (get(lessonType, 'availableTimes') || []).map(t => t.startDateTime),
        timezone,
      ),
    ).sort()
    return (
      resolveSelectedDateFromQuery({
        selectedDay,
        timezone,
        datesWithAvailability,
      }) ??
      DateTime.fromISO(datesWithAvailability[0] || DateTime.now(), {
        zone: timezone,
      })
    )
  })
  const [selectedSlot, setSelectedSlot] = useState(null)
  // Availability is only loaded a month ahead; this is the span it covers.
  const [loadedRange, setLoadedRange] = useState(() => {
    const { startDateTime, endDateTime } = initialAvailabilityDateRange()
    return { start: startDateTime, end: endDateTime }
  })
  const [calendarAnchor, setCalendarAnchor] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [verifiedAccessCode, setVerifiedAccessCode] = useState(
    !requiresAccessCode || location?.state?.verifiedAccessCode,
  )

  const selectedIso = selectedDate.toISODate()
  const timeSlotsForSelectedDate = timeSlotsByDate[selectedIso] || []
  const slotsByPart = groupSlotsByDayPart(timeSlotsForSelectedDate, timezone)
  const activeSlot = timeSlotsForSelectedDate.includes(selectedSlot)
    ? selectedSlot
    : null

  const days = useMemo(
    () =>
      buildDateStripDays({
        start: loadedRange.start,
        end: loadedRange.end,
        timezone,
        countsByDate,
      }),
    [loadedRange, timezone, countsByDate],
  )

  const formattedTimezone = toThreeLetterTimezone(timezone, selectedDate)
  const showTimezone = formattedTimezone !== null
  const allowedToOverwriteSchedule =
    user?.coach?.externalId === idOrSlug ||
    user?.coach?.coachProfile?.slug === idOrSlug

  const eventObject = {
    event: 'select-booking-time',
    title: get(lessonTypes, [0, 'title']),
    category: get(lessonTypes, [0, 'category']),
    facility: get(lessonTypes, ['0', 'facilities', '0', 'name']),
    location: `${get(lessonTypes, ['0', 'facilities', '0', 'city'])}, ${get(
      lessonTypes,
      ['0', 'facilities', '0', 'state'],
    )}`,
    price: `${get(lessonTypes, ['0', 'priceInCents']) / 100}`,
    event_date: null,
    event_time: null,
    duration: get(lessonTypes, [0, 'durationInMinutes']),
    coach_id: get(lessonTypes, ['0', 'coach', 'id']),
    coach_name: get(lessonTypes, ['0', 'coach', 'name']),
    access_code_required: get(lessonTypes, [0, 'requiresAccessCode'])
      ? 'yes'
      : 'no',
  }

  // Fire once per booking attempt (on Continue), matching the legacy layout
  // where a slot tap navigated straight to the next step.
  const handleContinue = () => {
    const slotDateTime = DateTime.fromISO(activeSlot, { zone: timezone })
    gtmEvent({
      ...eventObject,
      event_time: slotDateTime.toFormat('hh:mm a'),
      event_date: slotDateTime.toFormat('yyyy-LL-dd'),
    })
    // Remove the below event in the future.
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'selected-booking-time',
    })
  }

  const monthRequest = useRef(0)

  const handleMonthChange = async month => {
    const requestId = ++monthRequest.current
    const range = dateRangeForMonth(month, DateTime.local())
    setIsLoading(true)
    let result
    try {
      result = await refetch(range)
    } catch (error) {
      // Keep the previous range and selection when the refetch fails.
      return
    } finally {
      if (requestId === monthRequest.current) setIsLoading(false)
    }
    // A newer month request superseded this one.
    if (requestId !== monthRequest.current) return
    setLoadedRange({ start: range.startDateTime, end: range.endDateTime })
    // Land on the first open day of the new month when there is one.
    const { filteredTimeSlots: fetchedSlots } = getSlotSource(
      result?.data?.lessonTypes,
      nextExpirationDate,
    )
    const [firstOpenDate] = Object.keys(
      aggregateByDate(fetchedSlots, timezone),
    ).sort()
    setSelectedDate(
      firstOpenDate
        ? DateTime.fromISO(firstOpenDate, { zone: timezone })
        : range.startDateTime,
    )
  }

  const practiceSessionTypeId = isBayReservation
    ? get(lessonTypes, ['0', 'practiceSessionTypes', '0', 'id'])
    : ''
  const urlWithPracticeSessionTypeId = practiceSessionTypeId
    ? `&practiceSessionTypeId=${practiceSessionTypeId}`
    : ''
  const continueUrl = activeSlot
    ? `${currentUrl}${searchQuery}${activeSlot}${urlWithPracticeSessionTypeId}`
    : null

  const heading = (
    <>
      <Typography variant="h3" component="h1" className={classes.heading}>
        Choose a time
      </Typography>
      <p className={classes.subtitle}>
        {showTimezone
          ? `Times shown in the facility's time zone (${formattedTimezone}).`
          : "Times shown in the facility's time zone."}
      </p>
    </>
  )

  const summaryCard = (
    <BookingSummaryCard
      lessonType={lessonType}
      coach={coach}
      selectedSlot={activeSlot}
      timezone={timezone}
    />
  )

  return (
    <SchedulePageBackground color={PAGE_BACKGROUND} className={classes.page}>
      <div className={classes.inner}>
        {isCompact ? (
          <div className={classes.mobileTopCard}>
            <div className={classes.mobileBackRow}>
              <BackButton previousUrl={previousUrl} />
            </div>
            <MobileBookingSummary lessonType={lessonType} coach={coach} />
          </div>
        ) : (
          <div className={classes.desktopBack}>
            <BackButton previousUrl={previousUrl} />
          </div>
        )}
        <div className={classes.grid}>
          <div className={classes.mainCol}>
            {heading}
            <DateStrip
              days={days}
              selectedIso={selectedIso}
              showArrows={!isCompact}
              onSelect={day => setSelectedDate(day.dateTime)}
              onOpenCalendar={event => setCalendarAnchor(event.currentTarget)}
            />
            <CalendarPopover
              anchorEl={calendarAnchor}
              onClose={() => setCalendarAnchor(null)}
              selectedDate={selectedDate}
              onChange={date => {
                setSelectedDate(date)
                setCalendarAnchor(null)
              }}
              onMonthChange={handleMonthChange}
              shouldDisableDate={day =>
                !(day.toISODate() in timeSlotsByDate) &&
                day.toISODate() !== selectedIso
              }
              loading={isLoading}
            />
            <section
              className={classes.timeCard}
              aria-label="Available times"
              data-testid="time-card"
            >
              <Typography
                variant="h5"
                component="h2"
                className={classes.timeCardTitle}
              >
                {selectedDate.setLocale('en-US').toFormat('cccc, LLLL d')}
              </Typography>
              {timeSlotsForSelectedDate.length > 0 ? (
                <TimeSlotGroups
                  slotsByPart={slotsByPart}
                  timezone={timezone}
                  selectedSlot={activeSlot}
                  onSelect={setSelectedSlot}
                />
              ) : (
                <div className={classes.noTimes}>
                  <NoTimesAvailable
                    nextSlot={timeSlots[0]}
                    timezone={timezone}
                    showTimezone={showTimezone}
                    classes={legacyClasses}
                    coach={coach}
                    setShowRequestTime={setShowRequestTime}
                    isBayReservation={isBayReservation}
                    setSelectedDate={setSelectedDate}
                  />
                </div>
              )}
            </section>
            <div className={classes.bottomBar}>
              <p
                className={clsx(
                  classes.bottomText,
                  activeSlot && classes.bottomTextSelected,
                )}
                aria-live="polite"
              >
                {activeSlot
                  ? formatSelectedSlot(activeSlot, timezone)
                  : 'Select a time to continue'}
              </p>
              {continueUrl ? (
                <Button
                  component={Link}
                  to={continueUrl}
                  onClick={handleContinue}
                  role="link"
                  variant="contained"
                  color="primary"
                  className={classes.continueButton}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.continueButton}
                  disabled
                >
                  Continue
                </Button>
              )}
            </div>
            {allowedToOverwriteSchedule && (
              <div className={classes.customTime}>
                <p>
                  Don't see the time you're looking for? Override your
                  availability by adding a custom time.
                </p>
                <OverwriteAvailabilityDialog
                  trigger={<Button color="primary">Add Custom Time</Button>}
                  currentUrl={currentUrl}
                  durationInMinutes={durationInMinutes}
                  timezone={timezone}
                />
              </div>
            )}
          </div>
          {!isCompact && <div className={classes.sideCol}>{summaryCard}</div>}
        </div>
      </div>
      <VerifyAccessCode
        lessonType={lessonType}
        isOpen={!verifiedAccessCode && !lessonCreditBalanceQuantity}
        setIsOpen={setVerifiedAccessCode}
        coachName={coach.name}
        setVerifiedAccessCode={setVerifiedAccessCode}
      />
    </SchedulePageBackground>
  )
}

SelectTimeContent.propTypes = {
  lessonTypes: PropTypes.array.isRequired,
  currentUrl: PropTypes.string,
  previousUrl: PropTypes.string,
  refetch: PropTypes.func,
  setShowRequestTime: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  lessonCreditBalanceQuantity: PropTypes.string,
  nextExpirationDate: PropTypes.string,
}

// Redesigned "Choose a time" step of private lesson booking, shown when
// FLAG_FEAT_PRIVATE_BOOKING_REDESIGN is on. The legacy layout lives in
// ../select-time-layout.js.
const refreshTheme = extendTheme(themes.offeringsRefresh)

const SelectTimeLayoutRedesign = props => (
  <ThemeProvider theme={refreshTheme}>
    <SelectTimeContent {...props} />
  </ThemeProvider>
)

export default SelectTimeLayoutRedesign
